import React, { useState } from 'react';
import { MainLayout } from '../layouts/MainLayout';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Server, Network, HardDrive, Cpu, Plus, Play, Pause, CheckCircle, XCircle, Clock, Settings, Copy, Trash2, User, Users, Shield, Briefcase } from 'lucide-react';
interface ProvisioningTemplate {
  id: string;
  name: string;
  category: 'hardware' | 'user';
  type: 'server' | 'network' | 'workstation' | 'storage' | 'admin' | 'developer' | 'support' | 'manager';
  description: string;
  resources?: {
    cpu: number;
    ram: number;
    storage: number;
  };
  permissions?: string[];
  applications?: string[];
  status: 'active' | 'draft';
  usageCount: number;
}
interface ProvisioningJob {
  id: string;
  templateName: string;
  targetName: string;
  category: 'hardware' | 'user';
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  startedAt: string;
  completedAt?: string;
  error?: string;
}
const mockTemplates: ProvisioningTemplate[] = [
// Hardware Templates
{
  id: 't1',
  name: 'Standard Server',
  category: 'hardware',
  type: 'server',
  description: 'Standard-Serverkonfiguration für Web-Anwendungen',
  resources: {
    cpu: 4,
    ram: 16,
    storage: 500
  },
  status: 'active',
  usageCount: 24
}, {
  id: 't2',
  name: 'Datenbank Server',
  category: 'hardware',
  type: 'server',
  description: 'Hochleistungs-Datenbank mit SSD-Storage',
  resources: {
    cpu: 8,
    ram: 32,
    storage: 1000
  },
  status: 'active',
  usageCount: 12
}, {
  id: 't3',
  name: 'Netzwerk Switch',
  category: 'hardware',
  type: 'network',
  description: 'Managed Switch mit VLAN-Konfiguration',
  resources: {
    cpu: 2,
    ram: 4,
    storage: 50
  },
  status: 'active',
  usageCount: 8
}, {
  id: 't4',
  name: 'Entwickler Workstation',
  category: 'hardware',
  type: 'workstation',
  description: 'Vorkonfigurierte Entwicklungsumgebung',
  resources: {
    cpu: 6,
    ram: 24,
    storage: 750
  },
  status: 'active',
  usageCount: 45
},
// User Templates
{
  id: 'u1',
  name: 'Administrator',
  category: 'user',
  type: 'admin',
  description: 'Vollständige System- und Netzwerkadministrationsrechte',
  permissions: ['System Admin', 'User Management', 'Network Config', 'Security Settings'],
  applications: ['Active Directory', 'VMware', 'Monitoring Tools', 'Backup Software'],
  status: 'active',
  usageCount: 5
}, {
  id: 'u2',
  name: 'Software Entwickler',
  category: 'user',
  type: 'developer',
  description: 'Entwicklungsumgebung mit IDE und Entwicklertools',
  permissions: ['Code Repository', 'Dev Environment', 'Testing Tools', 'API Access'],
  applications: ['VS Code', 'Git', 'Docker', 'Postman', 'Slack'],
  status: 'active',
  usageCount: 32
}, {
  id: 'u3',
  name: 'IT Support',
  category: 'user',
  type: 'support',
  description: 'Support-Tools und eingeschränkte Admin-Rechte',
  permissions: ['Ticket System', 'Remote Desktop', 'User Support', 'Basic Config'],
  applications: ['Ticketing System', 'TeamViewer', 'Knowledge Base', 'Chat Support'],
  status: 'active',
  usageCount: 18
}, {
  id: 'u4',
  name: 'Projektmanager',
  category: 'user',
  type: 'manager',
  description: 'Projektmanagement-Tools und Reporting-Zugriff',
  permissions: ['Project Management', 'Team Overview', 'Reports', 'Resource Planning'],
  applications: ['Jira', 'Confluence', 'MS Project', 'Power BI', 'Teams'],
  status: 'active',
  usageCount: 12
}];
const mockJobs: ProvisioningJob[] = [{
  id: 'j1',
  templateName: 'Standard Server',
  targetName: 'web-server-prod-03',
  category: 'hardware',
  status: 'running',
  progress: 65,
  startedAt: '2024-01-15 14:30'
}, {
  id: 'j2',
  templateName: 'Software Entwickler',
  targetName: 'max.mueller@neocube.de',
  category: 'user',
  status: 'completed',
  progress: 100,
  startedAt: '2024-01-15 13:15',
  completedAt: '2024-01-15 13:18'
}, {
  id: 'j3',
  templateName: 'Entwickler Workstation',
  targetName: 'dev-ws-mueller',
  category: 'hardware',
  status: 'failed',
  progress: 45,
  startedAt: '2024-01-15 12:00',
  completedAt: '2024-01-15 12:23',
  error: 'Netzwerkverbindung unterbrochen'
}];
interface HardwareProvisioningProps {
  onLogout: () => void;
}
export function HardwareProvisioning({
  onLogout
}: HardwareProvisioningProps) {
  const [templates] = useState<ProvisioningTemplate[]>(mockTemplates);
  const [jobs] = useState<ProvisioningJob[]>(mockJobs);
  const [activeTab, setActiveTab] = useState<'templates' | 'jobs'>('templates');
  const [templateFilter, setTemplateFilter] = useState<'all' | 'hardware' | 'user'>('all');
  const filteredTemplates = templates.filter(t => templateFilter === 'all' || t.category === templateFilter);
  const getTypeIcon = (template: ProvisioningTemplate) => {
    if (template.category === 'hardware') {
      switch (template.type) {
        case 'server':
          return <Server className="h-5 w-5" data-id="element-673" />;
        case 'network':
          return <Network className="h-5 w-5" data-id="element-674" />;
        case 'workstation':
          return <Cpu className="h-5 w-5" data-id="element-675" />;
        case 'storage':
          return <HardDrive className="h-5 w-5" data-id="element-676" />;
      }
    } else {
      switch (template.type) {
        case 'admin':
          return <Shield className="h-5 w-5" data-id="element-677" />;
        case 'developer':
          return <Cpu className="h-5 w-5" data-id="element-678" />;
        case 'support':
          return <Users className="h-5 w-5" data-id="element-679" />;
        case 'manager':
          return <Briefcase className="h-5 w-5" data-id="element-680" />;
      }
    }
  };
  const getTypeColor = (template: ProvisioningTemplate) => {
    if (template.category === 'hardware') {
      switch (template.type) {
        case 'server':
          return 'bg-blue-100 text-blue-700';
        case 'network':
          return 'bg-purple-100 text-purple-700';
        case 'workstation':
          return 'bg-teal-100 text-teal-700';
        case 'storage':
          return 'bg-amber-100 text-amber-700';
      }
    } else {
      switch (template.type) {
        case 'admin':
          return 'bg-red-100 text-red-700';
        case 'developer':
          return 'bg-indigo-100 text-indigo-700';
        case 'support':
          return 'bg-emerald-100 text-emerald-700';
        case 'manager':
          return 'bg-orange-100 text-orange-700';
      }
    }
  };
  const getStatusBadge = (status: ProvisioningJob['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" data-id="element-681">
            <Clock className="h-3 w-3 mr-1" data-id="element-682" />
            Wartend
          </Badge>;
      case 'running':
        return <Badge variant="info" data-id="element-683">
            <Play className="h-3 w-3 mr-1" data-id="element-684" />
            Läuft
          </Badge>;
      case 'completed':
        return <Badge variant="success" data-id="element-685">
            <CheckCircle className="h-3 w-3 mr-1" data-id="element-686" />
            Abgeschlossen
          </Badge>;
      case 'failed':
        return <Badge variant="danger" data-id="element-687">
            <XCircle className="h-3 w-3 mr-1" data-id="element-688" />
            Fehlgeschlagen
          </Badge>;
    }
  };
  return <MainLayout title="Provisioning Automation" onLogout={onLogout} data-id="element-689">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8" data-id="element-690">
        <Card data-id="element-691">
          <div className="flex items-center justify-between" data-id="element-692">
            <div data-id="element-693">
              <p className="text-sm text-slate-500 mb-1" data-id="element-694">Hardware Templates</p>
              <p className="text-2xl font-bold text-slate-900" data-id="element-695">
                {templates.filter(t => t.category === 'hardware' && t.status === 'active').length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg" data-id="element-696">
              <Server className="h-6 w-6 text-blue-600" data-id="element-697" />
            </div>
          </div>
        </Card>

        <Card data-id="element-698">
          <div className="flex items-center justify-between" data-id="element-699">
            <div data-id="element-700">
              <p className="text-sm text-slate-500 mb-1" data-id="element-701">Benutzer Templates</p>
              <p className="text-2xl font-bold text-slate-900" data-id="element-702">
                {templates.filter(t => t.category === 'user' && t.status === 'active').length}
              </p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-lg" data-id="element-703">
              <User className="h-6 w-6 text-indigo-600" data-id="element-704" />
            </div>
          </div>
        </Card>

        <Card data-id="element-705">
          <div className="flex items-center justify-between" data-id="element-706">
            <div data-id="element-707">
              <p className="text-sm text-slate-500 mb-1" data-id="element-708">Laufende Jobs</p>
              <p className="text-2xl font-bold text-slate-900" data-id="element-709">
                {jobs.filter(j => j.status === 'running').length}
              </p>
            </div>
            <div className="p-3 bg-teal-100 rounded-lg" data-id="element-710">
              <Play className="h-6 w-6 text-teal-600" data-id="element-711" />
            </div>
          </div>
        </Card>

        <Card data-id="element-712">
          <div className="flex items-center justify-between" data-id="element-713">
            <div data-id="element-714">
              <p className="text-sm text-slate-500 mb-1" data-id="element-715">Erfolgsrate</p>
              <p className="text-2xl font-bold text-slate-900" data-id="element-716">94%</p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-lg" data-id="element-717">
              <CheckCircle className="h-6 w-6 text-emerald-600" data-id="element-718" />
            </div>
          </div>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-slate-200" data-id="element-719">
        <button onClick={() => setActiveTab('templates')} className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${activeTab === 'templates' ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-600 hover:text-slate-900'}`} data-id="element-720">
          Provisioning Templates
        </button>
        <button onClick={() => setActiveTab('jobs')} className={`px-4 py-2 font-medium text-sm transition-colors border-b-2 ${activeTab === 'jobs' ? 'border-teal-600 text-teal-600' : 'border-transparent text-slate-600 hover:text-slate-900'}`} data-id="element-721">
          Aktive Jobs
        </button>
      </div>

      {/* Templates View */}
      {activeTab === 'templates' && <Card title="Provisioning Templates" action={<div className="flex gap-2" data-id="element-723">
              <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-id="element-724">
                <button onClick={() => setTemplateFilter('all')} className={`px-3 py-1 text-sm font-medium rounded transition-colors ${templateFilter === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-725">
                  Alle
                </button>
                <button onClick={() => setTemplateFilter('hardware')} className={`px-3 py-1 text-sm font-medium rounded transition-colors ${templateFilter === 'hardware' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-726">
                  Hardware
                </button>
                <button onClick={() => setTemplateFilter('user')} className={`px-3 py-1 text-sm font-medium rounded transition-colors ${templateFilter === 'user' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-727">
                  Benutzer
                </button>
              </div>
              <Button size="sm" leftIcon={<Plus className="h-4 w-4" data-id="element-729" />} data-id="element-728">
                Neues Template
              </Button>
            </div>} data-id="element-722">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-730">
            {filteredTemplates.map(template => <div key={template.id} className="border border-slate-200 rounded-lg p-4 hover:border-teal-300 transition-colors" data-id="element-731">
                <div className="flex items-start justify-between mb-3" data-id="element-732">
                  <div className="flex items-center gap-3" data-id="element-733">
                    <div className={`p-2 rounded-lg ${getTypeColor(template)}`} data-id="element-734">
                      {getTypeIcon(template)}
                    </div>
                    <div data-id="element-735">
                      <div className="flex items-center gap-2" data-id="element-736">
                        <h3 className="font-semibold text-slate-900" data-id="element-737">
                          {template.name}
                        </h3>
                        <Badge variant={template.category === 'hardware' ? 'info' : 'secondary'} data-id="element-738">
                          {template.category === 'hardware' ? 'Hardware' : 'Benutzer'}
                        </Badge>
                      </div>
                      <p className="text-xs text-slate-500 mt-1" data-id="element-739">
                        {template.description}
                      </p>
                    </div>
                  </div>
                </div>

                {template.category === 'hardware' && template.resources && <div className="grid grid-cols-3 gap-3 mb-4 text-sm" data-id="element-740">
                    <div className="bg-slate-50 rounded p-2" data-id="element-741">
                      <p className="text-xs text-slate-500" data-id="element-742">CPU</p>
                      <p className="font-semibold text-slate-900" data-id="element-743">
                        {template.resources.cpu} Cores
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded p-2" data-id="element-744">
                      <p className="text-xs text-slate-500" data-id="element-745">RAM</p>
                      <p className="font-semibold text-slate-900" data-id="element-746">
                        {template.resources.ram} GB
                      </p>
                    </div>
                    <div className="bg-slate-50 rounded p-2" data-id="element-747">
                      <p className="text-xs text-slate-500" data-id="element-748">Storage</p>
                      <p className="font-semibold text-slate-900" data-id="element-749">
                        {template.resources.storage} GB
                      </p>
                    </div>
                  </div>}

                {template.category === 'user' && <div className="mb-4 space-y-3" data-id="element-750">
                    <div data-id="element-751">
                      <p className="text-xs font-medium text-slate-500 mb-2" data-id="element-752">
                        Berechtigungen:
                      </p>
                      <div className="flex flex-wrap gap-1" data-id="element-753">
                        {template.permissions?.slice(0, 3).map((perm, idx) => <span key={idx} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded" data-id="element-754">
                            {perm}
                          </span>)}
                        {template.permissions && template.permissions.length > 3 && <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded" data-id="element-755">
                              +{template.permissions.length - 3}
                            </span>}
                      </div>
                    </div>
                    <div data-id="element-756">
                      <p className="text-xs font-medium text-slate-500 mb-2" data-id="element-757">
                        Anwendungen:
                      </p>
                      <div className="flex flex-wrap gap-1" data-id="element-758">
                        {template.applications?.slice(0, 3).map((app, idx) => <span key={idx} className="text-xs px-2 py-1 bg-emerald-50 text-emerald-700 rounded" data-id="element-759">
                            {app}
                          </span>)}
                        {template.applications && template.applications.length > 3 && <span className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded" data-id="element-760">
                              +{template.applications.length - 3}
                            </span>}
                      </div>
                    </div>
                  </div>}

                <div className="flex items-center justify-between pt-3 border-t border-slate-100" data-id="element-761">
                  <span className="text-xs text-slate-500" data-id="element-762">
                    {template.usageCount}x verwendet
                  </span>
                  <div className="flex gap-2" data-id="element-763">
                    <Button variant="ghost" size="sm" data-id="element-764">
                      <Copy className="h-4 w-4" data-id="element-765" />
                    </Button>
                    <Button variant="ghost" size="sm" data-id="element-766">
                      <Settings className="h-4 w-4" data-id="element-767" />
                    </Button>
                    <Button variant="outline" size="sm" leftIcon={<Play className="h-4 w-4" data-id="element-769" />} data-id="element-768">
                      Bereitstellen
                    </Button>
                  </div>
                </div>
              </div>)}
          </div>
        </Card>}

      {/* Jobs View */}
      {activeTab === 'jobs' && <Card title="Provisioning Jobs" data-id="element-770">
          <div className="space-y-4" data-id="element-771">
            {jobs.map(job => <div key={job.id} className="border border-slate-200 rounded-lg p-4" data-id="element-772">
                <div className="flex items-center justify-between mb-3" data-id="element-773">
                  <div data-id="element-774">
                    <div className="flex items-center gap-2" data-id="element-775">
                      <h3 className="font-semibold text-slate-900" data-id="element-776">
                        {job.targetName}
                      </h3>
                      <Badge variant={job.category === 'hardware' ? 'info' : 'secondary'} data-id="element-777">
                        {job.category === 'hardware' ? 'Hardware' : 'Benutzer'}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-500" data-id="element-778">
                      Template: {job.templateName}
                    </p>
                  </div>
                  {getStatusBadge(job.status)}
                </div>

                {job.status === 'running' && <div className="mb-3" data-id="element-779">
                    <div className="flex items-center justify-between text-sm mb-1" data-id="element-780">
                      <span className="text-slate-600" data-id="element-781">Fortschritt</span>
                      <span className="font-semibold text-slate-900" data-id="element-782">
                        {job.progress}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-783">
                      <div className="bg-teal-600 h-2 rounded-full transition-all duration-300" style={{
                width: `${job.progress}%`
              }} data-id="element-784"></div>
                    </div>
                  </div>}

                {job.error && <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-785">
                    <p className="text-sm text-red-700" data-id="element-786">
                      <strong data-id="element-787">Fehler:</strong> {job.error}
                    </p>
                  </div>}

                <div className="flex items-center justify-between text-xs text-slate-500" data-id="element-788">
                  <span data-id="element-789">Gestartet: {job.startedAt}</span>
                  {job.completedAt && <span data-id="element-790">Beendet: {job.completedAt}</span>}
                  <div className="flex gap-2" data-id="element-791">
                    {job.status === 'running' && <Button variant="ghost" size="sm" data-id="element-792">
                        <Pause className="h-4 w-4" data-id="element-793" />
                      </Button>}
                    {job.status === 'failed' && <Button variant="outline" size="sm" data-id="element-794">
                        <Play className="h-4 w-4 mr-1" data-id="element-795" />
                        Wiederholen
                      </Button>}
                    <Button variant="ghost" size="sm" data-id="element-796">
                      <Trash2 className="h-4 w-4" data-id="element-797" />
                    </Button>
                  </div>
                </div>
              </div>)}
          </div>
        </Card>}
    </MainLayout>;
}